import { animationTransition, animationTransitionExt } from '@shared/utils/animationTransition';
import { useForkRef } from '@shared/utils/hooks/useCombinedRefs';
import { Box } from '../Box';
import { Flex } from '../Flex';
import type { InputV2Props } from '../InputV2/Input.types';
import { InputV2 } from '../InputV2/Input';
import { pxToRem, useTheme } from '@withjoy/joykit/theme';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTimeout } from '@shared/hooks/useTimeout';

const INPUT_FIELD_HEIGHT = 56;
const VISIBLE_INPUT_FIELD_HEIGHT = 12;
const INPUT_FIELD_VERTICAL_SPACE = 24;

interface InputV2InlineLabelProps
  extends InputV2Props,
    Readonly<{
      label: string;
    }> {}

export const InputV2InlineLabel = forwardRef<HTMLInputElement, InputV2InlineLabelProps>((baseProps, ref) => {
  const { label, ...inputProps } = baseProps;
  const [states, setStates] = useState<{ isFocused: boolean; isVisibleField: boolean }>({ isFocused: false, isVisibleField: !!baseProps.value });
  const inputRef = useRef<HTMLInputElement>(null);
  const forkedRefHandler = useForkRef(ref, inputRef);
  const { colors } = useTheme();
  const { set: setTimeoutFn, clear: clearTimeoutFn } = useTimeout();

  const isActiveState = baseProps.isInvalid || states.isFocused;

  const handleOnBlur = useEventCallback(() => {
    setStates({ isVisibleField: !!baseProps.value, isFocused: false });
  });

  const handleOnFocus = useEventCallback(() => {
    setStates({ isVisibleField: true, isFocused: true });
  });

  const handleOnClick = useEventCallback(() => {
    inputRef.current?.focus();
  });

  // Used to make visible field with animation on autocomplete fields on initial render
  useEffect(() => {
    setTimeoutFn(() => {
      if (inputRef.current && inputRef.current.value) {
        setStates(prev => ({
          ...prev,
          isVisibleField: true,
        }));
      }
    }, 200); // Wait for autofill to complete

    return () => {
      clearTimeoutFn();
    };
  }, [setTimeoutFn, clearTimeoutFn]);

  // Sync label position with external value changes (e.g., formik.resetForm())
  useEffect(() => {
    setStates(prev => ({
      ...prev,
      isVisibleField: !!baseProps.value,
    }));
  }, [baseProps.value]);

  return (
    <Box
      width={'100%'}
      position={'relative'}
      role={'group'}
    >
      <InputV2
        aria-label={label}
        {...inputProps}
        ref={forkedRefHandler}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        overrides={{
          Input: {
            props: {
              typographyVariant: 'body2',
              color: 'mono14',
              paddingTop: 5,
              paddingBottom: 0,
              height: pxToRem(INPUT_FIELD_HEIGHT),
            },
          },
          Root: {
            props: {
              border: 'none',
              overflow: 'hidden',
              borderRadius: pxToRem(12),
              transition: animationTransition('box-shadow'),
              boxShadow: `0px 0 0px ${isActiveState ? 2 : 1}px ${baseProps.isInvalid ? colors.inputBorderError : states.isFocused ? colors.newUIBrandPrimary : colors.mono5}`,
              _groupHover: { ...(!isActiveState && { boxShadow: `0px 0 0px 1px ${colors.mono6}` }) },
              _after: {
                borderRadius: pxToRem(12),
                content: '" "',
                height: '100%',
                width: '100%',
                position: 'absolute',
                pointerEvents: 'none',
              },
            },
          },
        }}
      />
      <Flex
        position="absolute"
        top={4}
        left={5}
        backgroundColor="transparent"
        alignItems="center"
        color={states.isVisibleField ? 'mono10' : 'mono8'}
        fontSize={pxToRem(states.isVisibleField ? VISIBLE_INPUT_FIELD_HEIGHT : 15)}
        lineHeight={pxToRem(states.isVisibleField ? VISIBLE_INPUT_FIELD_HEIGHT : INPUT_FIELD_HEIGHT - INPUT_FIELD_VERTICAL_SPACE)}
        transition={animationTransitionExt({ property: 'all', duration: '0.2s', timingFunction: 'ease-out' })}
        onClick={handleOnClick}
      >
        {label}
      </Flex>
    </Box>
  );
});
